import { FormBuilder, FormControl, FormGroup, Validators, FormArray } from '@angular/forms';
import { Component, OnInit, Inject } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';

import { Question } from '@shared/models/question';
import { sector } from '@app/shared/models/sector';
import { RadioType } from './interfaces/radio.interface';
import { InsertPosition } from './interfaces/insert-position.interface';
import { Guid } from '@app/shared/models/guid';

import { QuestionnaireService } from '@app/shared/services/questionnaire.service';
import { NotificationService } from '@app/shared/components/notification/notification.service';
import { HelperService } from '@app/shared/services/helper.service';
import { ValidateTagValidator } from '@app/shared/shared.module';

@Component({
  selector: 'db-question-dialog',
  templateUrl: './question-dialog.component.html',
  styleUrls: ['./question-dialog.component.scss']
})
export class QuestionDialogComponent implements OnInit {
  static readonly DEFAULT_CHECKBOX_LABEL = 'Include?';

  dialogTitle: string;
  submitButtonLabel: string;

  tabs: any;
  question: Question;
  questionForm: FormGroup;
  radios: RadioType[];
  complete: any;

  existingFormTags: string[];
  sectors$: Observable<Array<sector>>;
  selectedSectors: any;
  selectedLinkedResponsiblity: string;
  questionTypes: any[] = [];
  selectedQuestionType = '';

  questionPositionDropdownList: any[] = [];
  sectorDropdownList: any[] = [];
  tabPositionDropdownList: any[] = [];
  linkedHealthAndSafetyResponsibilitiesQuestionDropdownList: any[];
  insertAnchorDropdownList: { id: string; label: string }[] = [];

  questionPositionDropdownSettings = {};
  sectorDropdownSettings = {};
  tabPositionDropdownSettings = {};
  typeDropdownSettings = {};
  linkedHealthAndSafetyResponsibilitiesQuestionDropdownSettings = {};

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<QuestionDialogComponent>,
    private service: QuestionnaireService,
    private notificationService: NotificationService,
    private _helpersService: HelperService,
    @Inject(MAT_DIALOG_DATA) private data: any
  ) {}

  ngOnInit() {
    this.tabs = this.data.tabs;

    this.tabPositionDropdownList = this.tabs;
    this.questionPositionDropdownList = this.tabs[0].fields;
    this.question = this.data.question;

    this.radios = [{ label: 'Radio 1', key: this.question.id + '_Radio1' }];

    this.setDialogData();
    this.initializeDropdowns();

    this.existingFormTags = this._helpersService.findTagsFromForm(this.service.getCurrentQuestionnaire().Questionnaire, this.question);

    this.questionForm = this.formBuilder.group({
      label: [this.question.templateOptions.label, Validators.required],
      type: [this.question.type, this.question.newField ? Validators.required : Validators.nullValidator],
      sector: [this.service.getSectorAsObject(this.question.sector), Validators.required],
      required: this.question.templateOptions.required,
      tag: [this.question.tag, [ValidateTagValidator.validate(this.existingFormTags), Validators.required]],
      id: this.question.id,
      key: this.question.key,
      newField: this.question.newField,
      tabPosition: [this.question.tabPosition ? this.question.tabPosition : null],
      questionPosition: [this.question.questionPosition ? this.question.questionPosition : null],
      isLockDownEdit: [this.question.isLockDownEdit ? this.question.isLockDownEdit : false],
      checkboxLabel: [this.getExistingCheckboxLabel() || QuestionDialogComponent.DEFAULT_CHECKBOX_LABEL],
      linkedHealthAndSafetyResponsibilitiesQuestion: this.question.linkedHealthAndSafetyResponsibilitiesQuestion ?
        this.question.linkedHealthAndSafetyResponsibilitiesQuestion : null
    }, { validator: ValidateTagValidator.validateForm()});

    if (this.question.newField) {
      this.initializeInsertPosition(this.data.tabLabel, this.data.insertAt);
    }

    this.selectedSectors = this.service.getSectorAsObject(this.question.sector);
    this.questionForm.addControl(this.radios[0].key, new FormControl(this.radios[0].label));

    this.sectors$ = this.service.getSectors();

    this.checkQuestionType();
  }

  /**
   * The inner checkbox field of a saved checkbox question. The parent's type is removed
   * when the question is created, so on edit the type is only visible on the field group.
   */
  private getExistingCheckboxField(): Question | undefined {
    return (this.question.fieldGroup || []).find(member => member.type === 'checkbox');
  }

  private getExistingCheckboxLabel(): string {
    const checkboxField = this.getExistingCheckboxField();
    return checkboxField && checkboxField.templateOptions ? checkboxField.templateOptions.label : '';
  }

  isCheckboxQuestion(): boolean {
    return this.questionForm.get('type').value === 'customcheckbox' || !!this.getExistingCheckboxField();
  }

  setDialogData() {
    this.dialogTitle = this.question.newField ? 'Add Question' : 'Edit Question';
    this.submitButtonLabel = this.question.newField ? 'Add Question' : 'Save Question';
  }

  initializeDropdowns() {
    this.sectorDropdownSettings = {
      singleSelection: false,
      idField: 'id',
      textField: 'sectorName',
      itemsShowLimit: 1
    };

    this.typeDropdownSettings = {
      idField: 'value',
      textField: 'label'
    };

    this.tabPositionDropdownSettings = {
      idField: 'label',
      textField: 'label'
    };

    this.questionPositionDropdownSettings = {
      idField: 'id',
      textField: 'tag'
    };

    this.linkedHealthAndSafetyResponsibilitiesQuestionDropdownSettings = {
      idField: 'id',
      textField: 'label'
    };

    this.getSectors();
  }

  initializeInsertPosition(tabLabel: string, insertAt?: InsertPosition) {
    const tab = this.tabs.find(t => t.label === tabLabel);

    this.insertAnchorDropdownList = (tab ? tab.fields : []).map(field => ({
      id: field.id,
      label: this.getInsertAnchorLabel(field)
    }));

    const anchorExists = !!insertAt && this.insertAnchorDropdownList.some(a => a.id === insertAt.anchorId);

    this.questionForm.addControl('insertPosition', new FormControl(anchorExists ? insertAt.position : 'end'));
    this.questionForm.addControl('insertAnchorId', new FormControl(anchorExists ? insertAt.anchorId : null));

    this.updateInsertAnchorValidation();
  }

  updateInsertAnchorValidation() {
    const anchorControl = this.questionForm.get('insertAnchorId');

    if (this.questionForm.get('insertPosition').value === 'end') {
      anchorControl.clearValidators();
      anchorControl.setValue(null);
    } else {
      anchorControl.setValidators(Validators.required);
    }

    anchorControl.updateValueAndValidity();
  }

  private getInsertAnchorLabel(field: any): string {
    const label = (field.templateOptions && field.templateOptions.label) || '';
    const text = [field.tag, label].filter(Boolean).join(' - ') || field.id;

    return text.length > 90 ? text.substring(0, 87) + '...' : text;
  }

  getSectors() {
    this.service.getSectors().subscribe(result => {
      this.sectorDropdownList = result;
    });
  }

  getHAndSResponsibilitiesQuestions() {
    const filteredLinkedHSResQuestionDropdownList = [];

    this.tabs.filter(tab => {
      tab.fields.filter(field => {
        if (field.type === 'customResponsibilitiesList') {
          filteredLinkedHSResQuestionDropdownList.push({
            id: field.id,
            label: field.templateOptions.label
          });
        }
      });
    });

    this.linkedHealthAndSafetyResponsibilitiesQuestionDropdownList = filteredLinkedHSResQuestionDropdownList;

    if (this.question.linkedHealthAndSafetyResponsibilitiesQuestion) {
      this.questionForm.get('linkedHealthAndSafetyResponsibilitiesQuestion').setValue(this.filterLinkedQuestion());
    }

    this.questionForm.registerControl('multipleOutputs', this.createNewForm());
  }

  saveQuestion(questionForm) {
    this.question.templateOptions.label = questionForm.value.label;
    this.question.templateOptions.required = questionForm.value.required;
    this.question.isLockDownEdit = questionForm.value.isLockDownEdit;

    this.question.sector = '';

    for (let i = 0; i < this.questionForm.value.sector.length; i++) {
      this.question.sector += questionForm.value.sector[i].id + ';';
    }

    this.question.tag = questionForm.value.tag;

    if (this.questionForm.value.type === 'customHealthAndSafetyResponsibilities') {
      this.question.linkedHealthAndSafetyResponsibilitiesQuestion = this.questionForm.get('linkedHealthAndSafetyResponsibilitiesQuestion')
        .value[0].id
        ? this.questionForm.get('linkedHealthAndSafetyResponsibilitiesQuestion').value[0].id
        : null;

      const outputs = this.questionForm.value.multipleOutputs;
      this.question.templateOptions.multipleOutputs = outputs ? outputs.slice() : [];
    }

    if (this.question.newField) {
      this.question.type = questionForm.value.type;
    }

    if (this.isCheckboxQuestion()) {
      const checkboxLabel = (questionForm.value.checkboxLabel || '').trim();
      this.question.checkboxLabel = checkboxLabel || QuestionDialogComponent.DEFAULT_CHECKBOX_LABEL;
    } else {
      delete this.question.checkboxLabel;
    }

    if (this.question.type === 'customradio') {
      this.question.fieldGroup = [];
      for (let i = 0; i <= this.radios.length; i++) {
        const q: Question = new Question();
        if (i === 0) {
          q.key = this.question.id + 'radioOptions';
          q.isLockDownEdit = this.question.isLockDownEdit;
          q.id = q.key;
          q.type = 'radio';
          q.className = 'col-10';
          q.templateOptions.required = true;
          q.templateOptions.label = this.question.templateOptions.label;
          q.templateOptions.options = [];

          for (let j = 0; j < this.radios.length; j++) {
            q.templateOptions.options.push({ label: this.questionForm.value[this.radios[j].key], value: this.radios[j].key });
          }
        } else {
          q.key = this.question.id + '_Text' + i;
          q.isLockDownEdit = this.question.isLockDownEdit;
          q.id = q.key;
          q.type = 'customradio';
          q.className = 'col-2';
          q.hideExpression = 'model.' + this.question.id + 'radioOptions' + ' != "' + this.radios[i - 1].key + '"';
          q.templateOptions.label = 'Text';
        }
        this.question.fieldGroup.push(q);
      }
    }

    const insertAt: InsertPosition = this.question.newField
      ? { position: questionForm.value.insertPosition, anchorId: questionForm.value.insertAnchorId }
      : null;

    this.dialogRef.close(
      JSON.stringify({question: this.question, action: 'save', insertAt})
    );
  }

  resetQuestionTypeValues() {
    this.questionForm.patchValue({
      linkedHealthAndSafetyResponsibilitiesQuestion: null
    });

    this.questionForm.removeControl('multipleOutputs');
  }

  checkQuestionType() {
    this.resetQuestionTypeValues();
    this.updateCheckboxLabelValidation();

    switch (this.questionForm.get('type').value) {
      case 'textarea':
        return (this.selectedQuestionType = 'questionTypeTextarea');
      case 'customcheckbox':
        return (this.selectedQuestionType = 'questionTypeCheckbox');
      case 'customradio':
        return (this.selectedQuestionType = 'questionTypeRadio');
      case 'customResponsibilitiesList':
        return (this.selectedQuestionType = 'questionTypeResponsibilitiesList');
      case 'customHealthAndSafetyResponsibilities':
        this.getHAndSResponsibilitiesQuestions();
        return (this.selectedQuestionType = 'questionTypeHealthAndSafetyResponsibilities');
      default:
        this.resetQuestionTypeValues();
        // Saved checkbox questions have no parent type; detect them from the field group.
        this.selectedQuestionType = this.getExistingCheckboxField() ? 'questionTypeCheckbox' : null;
    }
  }

  private updateCheckboxLabelValidation() {
    const control = this.questionForm.get('checkboxLabel');

    if (this.isCheckboxQuestion()) {
      control.setValidators(Validators.required);
    } else {
      control.clearValidators();
    }

    control.updateValueAndValidity();
  }

  AddRadio() {
    const newControl = this.question.id + '_Radio' + (this.radios.length + 1);
    this.questionForm.addControl(newControl, new FormControl('Radio ' + (this.radios.length + 1), Validators.required));
    this.radios.push({ label: newControl, key: newControl });
    return false;
  }

  RemoveRadio(radio) {
    const index = this.radios.indexOf(radio);
    if (index !== -1) {
      this.radios.splice(index, 1);
    }

    this.questionForm.removeControl(radio.key);
    return false;
  }

  public deleteTextForm(index: number): void {
    const form = this.questionForm.get('multipleOutputs') as FormArray;
    form.removeAt(index);
  }

  addEntry(): void {
    const form = this.questionForm.get('multipleOutputs') as FormArray;
    form.push(this.createNewField());
  }

  private createNewForm(): FormArray {
    const form: FormArray = this.formBuilder.array([]) as FormArray;

    const field = this.question.templateOptions.multipleOutputs;

    if (field.length) {
      field.forEach((r: any) => {
        form.push(this.createNewField(r));
      });
    }
    else {
      form.push(this.createNewField());
    }

    return form;
  }

  private createNewField(output?: any): FormGroup {
    return this.formBuilder.group({
      id:  output ? output.id : Guid.newGuid(),
      output: [output ? output.output : null, [Validators.required]],
      tag: [output ? output.tag : null, [Validators.required, ValidateTagValidator.validate(this.existingFormTags)]],
    });
  }

  private filterLinkedQuestion() {
    return this.linkedHealthAndSafetyResponsibilitiesQuestionDropdownList.filter((l: any) => {
      return l.id === this.question.linkedHealthAndSafetyResponsibilitiesQuestion;
    })
  }
}
